import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Alert
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

const HelicopterFDPRules = ({ baseline, handleChange }) => {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h6">Helicopter-Specific FDP Rules (D7J-J2)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Helicopter operations have different FDP limits based on crew complement (single-pilot vs. two-pilot) and number of landings.
            These values are based on D7J-J2 - Helicopter.pdf regulations.
          </Typography>
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>Single-Pilot Operations</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Daily FDP Hours (Single-Pilot)"
              type="number"
              value={baseline.maxDailyFDPHoursSinglePilot}
              onChange={(e) => handleChange('maxDailyFDPHoursSinglePilot', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 14, step: 0.5 }}
              helperText="Single-pilot helicopter FDP limit"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Two-Pilot Operations (by Number of Landings)</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max FDP Hours (1 Landing)"
              type="number"
              value={baseline.maxDailyFDPHoursTwoPilot1Landing}
              onChange={(e) => handleChange('maxDailyFDPHoursTwoPilot1Landing', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 14, step: 0.5 }}
              helperText="Two-pilot with 1 landing: 13 hours"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max FDP Hours (2 Landings - Night)"
              type="number"
              value={baseline.maxDailyFDPHoursTwoPilot2Landings}
              onChange={(e) => handleChange('maxDailyFDPHoursTwoPilot2Landings', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 14, step: 0.5 }}
              helperText="Two-pilot with 2 landings (night): 12.5 hours"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max FDP Hours (3 Landings - Day)"
              type="number"
              value={baseline.maxDailyFDPHoursTwoPilot3Landings}
              onChange={(e) => handleChange('maxDailyFDPHoursTwoPilot3Landings', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 14, step: 0.5 }}
              helperText="Two-pilot with 3 landings (day): 12.5 hours"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Two-Pilot Flight Time Limits</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max Flight Time (1 Landing)"
              type="number"
              value={baseline.maxFlightTimeTwoPilot1Landing}
              onChange={(e) => handleChange('maxFlightTimeTwoPilot1Landing', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 12, step: 0.5 }}
              helperText="Two-pilot: 7 hours (PDF 6.1.2.a)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max Flight Time (2 Landings)"
              type="number"
              value={baseline.maxFlightTimeTwoPilot2Landings}
              onChange={(e) => handleChange('maxFlightTimeTwoPilot2Landings', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 12, step: 0.5 }}
              helperText="Two-pilot: 7 hours (PDF 6.1.2.a)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max Flight Time (3 Landings)"
              type="number"
              value={baseline.maxFlightTimeTwoPilot3Landings}
              onChange={(e) => handleChange('maxFlightTimeTwoPilot3Landings', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 12, step: 0.5 }}
              helperText="Two-pilot: 7 hours (PDF 6.1.2.a)"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Flight Time Limits (PDF 6.1.2)</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Flight Time (24 Consecutive Hours)"
              type="number"
              value={baseline.maxFlightTime24Hours}
              onChange={(e) => handleChange('maxFlightTime24Hours', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 12, step: 0.5 }}
              helperText="PDF 6.1.2.a: 7 hours maximum"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Flight Time (Single-Pilot in One Duty Period)"
              type="number"
              value={baseline.maxFlightTimeSinglePilot}
              onChange={(e) => handleChange('maxFlightTimeSinglePilot', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 12, step: 0.5 }}
              helperText="PDF 6.1.2 Note 1: 6 hours maximum"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Cumulative FDP Limits (PDF 6.1.1)</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max FDP (7 Consecutive Days)"
              type="number"
              value={baseline.maxFlightDutyPeriod7Days}
              onChange={(e) => handleChange('maxFlightDutyPeriod7Days', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 100, step: 0.5 }}
              helperText="PDF 6.1.1.b: 60 hours maximum"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Duty Time (28 Consecutive Days)"
              type="number"
              value={baseline.maxDutyTime28Days}
              onChange={(e) => handleChange('maxDutyTime28Days', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 500, step: 0.5 }}
              helperText="PDF 6.1.1.c: 200 hours maximum"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Maximum Landings (PDF 6.2)</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Max Landings (Per Duty Period)"
              type="number"
              value={baseline.maxLandingsPerDutyPeriod}
              onChange={(e) => handleChange('maxLandingsPerDutyPeriod', parseInt(e.target.value))}
              inputProps={{ min: 1, max: 100, step: 1 }}
              helperText="PDF 6.2: 50 landings maximum"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Landings/Hour Requiring Break"
              type="number"
              value={baseline.maxLandingsBreakRequiredRate}
              onChange={(e) => handleChange('maxLandingsBreakRequiredRate', parseFloat(e.target.value))}
              inputProps={{ min: 1, max: 20, step: 0.5 }}
              helperText="PDF 6.2: 10+ landings/hour requires break"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Break Duration (Minutes)"
              type="number"
              value={baseline.maxLandingsBreakDuration}
              onChange={(e) => handleChange('maxLandingsBreakDuration', parseInt(e.target.value))}
              inputProps={{ min: 15, max: 60, step: 1 }}
              helperText="PDF 6.2: 30 minutes within any 2-hour period"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Split Duty Rules</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={baseline.allowSplitDuty}
                  onChange={(e) => handleChange('allowSplitDuty', e.target.checked)}
                />
              }
              label="Allow Split Duty (Helicopter operations)"
            />
          </Grid>
          {baseline.allowSplitDuty && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Min Split Duty Break Hours"
                  type="number"
                  value={baseline.minSplitDutyBreakHours}
                  onChange={(e) => handleChange('minSplitDutyBreakHours', parseFloat(e.target.value))}
                  inputProps={{ min: 3, max: 10, step: 0.5 }}
                  helperText="Minimum break duration for split duty"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Split Duty Break Hours"
                  type="number"
                  value={baseline.maxSplitDutyBreakHours}
                  onChange={(e) => handleChange('maxSplitDutyBreakHours', parseFloat(e.target.value))}
                  inputProps={{ min: 3, max: 10, step: 0.5 }}
                  helperText="Maximum break duration for split duty"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Split Duty Accommodation Required Hours"
                  type="number"
                  value={baseline.splitDutyAccommodationRequiredHours}
                  onChange={(e) => handleChange('splitDutyAccommodationRequiredHours', parseFloat(e.target.value))}
                  inputProps={{ min: 6, max: 10, step: 0.5 }}
                  helperText="Break duration requiring accommodation (typically 6 hours)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Split Duty Segment Hours"
                  type="number"
                  value={baseline.maxSplitDutySegmentHours}
                  onChange={(e) => handleChange('maxSplitDutySegmentHours', parseFloat(e.target.value))}
                  inputProps={{ min: 1, max: 10, step: 0.5 }}
                  helperText="Maximum FDP for each segment (before/after break)"
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>Window of Circadian Low (WOCL)</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="WOCL Start Time"
              type="text"
              value={baseline.woclStart}
              onChange={(e) => handleChange('woclStart', e.target.value)}
              placeholder="02:00"
              helperText="WOCL start time (HH:mm format, default: 02:00)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="WOCL End Time"
              type="text"
              value={baseline.woclEnd}
              onChange={(e) => handleChange('woclEnd', e.target.value)}
              placeholder="06:00"
              helperText="WOCL end time (HH:mm format, default: 06:00)"
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default HelicopterFDPRules;
